import { type Breadcrumb, captureMessage, type ErrorEvent, type EventHint } from '@sentry/react';
import { AxiosError, isAxiosError } from 'axios';
import { FieldErrors } from 'react-hook-form';
import {
  failedChunkUrl,
  isDynamicImportError,
  isStaleChunkReloading,
  wasReloadRecentlyAttempted,
} from './dynamicImportError';
import { isPresignedUrl, stripPresignedQuery } from './presignedUrlScrub';

export function captureFormValidationError(formReference: string, errors: FieldErrors | unknown) {
  captureMessage(`Validation failed - ${formReference}:\n${JSON.stringify(errors)}`);
}

export function captureUnexpectedState(message: string, state: unknown) {
  captureMessage(`Unexpected state: ${message}\n${JSON.stringify(state)}`);
}

/** The API's normal answers (signed out, no access, not found, conflict), which the UI handles. */
const UNREPORTED_STATUSES = new Set([401, 403, 404, 409]);

/** No response at all: a reload cut the request off, the connection dropped or the caller cancelled. */
const UNREPORTED_CODES = new Set<string | undefined>([AxiosError.ERR_NETWORK, AxiosError.ERR_CANCELED]);

export function shouldReportToSentry(error: AxiosError): boolean {
  const status = error.response?.status;
  if (status === undefined) {
    return !UNREPORTED_CODES.has(error.code);
  }
  return !UNREPORTED_STATUSES.has(status) && !error.config?.skipSentryStatuses?.includes(status);
}

/** Routes that report a request failure without anyone choosing to: the axios interceptor's tagged
 *  capture, console capture and unhandled rejections. A deliberate captureException elsewhere still reports. */
export const REQUEST_ERROR_TYPE = {
  http: 'http_error',
  network: 'network_error',
  axios: 'axios_error',
} as const;
const INTERCEPTOR_TYPES = new Set<string>(Object.values(REQUEST_ERROR_TYPE));
const AUTOMATIC_MECHANISMS = new Set([
  'auto.core.capture_console',
  'auto.browser.global_handlers.onunhandledrejection',
]);

function isAutomaticReport(event: ErrorEvent): boolean {
  if (INTERCEPTOR_TYPES.has(String(event.tags?.type))) {
    return true;
  }
  return (
    event.exception?.values?.some((value) => AUTOMATIC_MECHANISMS.has(value.mechanism?.type ?? '')) ?? false
  );
}

/** What Chrome, Safari and Firefox say when a fetch gets no response. */
const FETCH_NETWORK_FAILURE_MESSAGES = new Set([
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.',
]);

/** Hosts we don't own, whose failed fetches we can't act on: Google Fonts, and Frontegg on any subdomain. */
const THIRD_PARTY_FETCH_HOSTS = {
  exact: ['fonts.gstatic.com', 'fonts.googleapis.com'],
  withSubdomains: ['frontegg.com'],
};

function isThirdPartyFetchHost(host: string): boolean {
  return (
    THIRD_PARTY_FETCH_HOSTS.exact.includes(host) ||
    THIRD_PARTY_FETCH_HOSTS.withSubdomains.some((domain) => host === domain || host.endsWith(`.${domain}`))
  );
}

/** Sentry's fetch instrumentation appends the request's host to the error itself, as in
 *  "Failed to fetch (fonts.gstatic.com)" (enhanceFetchErrorMessages: 'always', the default).
 *  With no host we can't tell whose request it was, so it reports. */
function isThirdPartyFetchFailure(error: unknown): boolean {
  const match = error instanceof TypeError ? /^(.+) \(([^\s()]+)\)$/.exec(error.message) : null;
  return !!match && FETCH_NETWORK_FAILURE_MESSAGES.has(match[1]) && isThirdPartyFetchHost(match[2]);
}

function isExpectedRequestFailure(error: unknown): boolean {
  return (isAxiosError(error) && !shouldReportToSentry(error)) || isThirdPartyFetchFailure(error);
}

/** beforeSend: drops request failures we don't act on when they arrive through an automatic route.
 *  Datadog still counts every axios one through emitHttpError. */
export function dropExpectedRequestFailures(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  return isExpectedRequestFailure(hint.originalException) && isAutomaticReport(event) ? null : event;
}

/** beforeSend: a chunk that fails to download while the page reloads for it is a deploy that replaced the
 *  assets under an open tab, and the reload fixes it. Any other chunk failure (most often the reload
 *  already ran and did not help) reports, tagged with the chunk and the release that asked for it. */
export function handleChunkLoadFailures(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  const error = hint.originalException;
  if (!isDynamicImportError(error)) {
    return event;
  }
  if (isStaleChunkReloading()) {
    return null;
  }
  return {
    ...event,
    tags: { ...event.tags, chunk_load_failure: true, chunk_reload_attempted: wasReloadRecentlyAttempted() },
    contexts: { ...event.contexts, chunk_load: { url: failedChunkUrl(error), release: event.release } },
  };
}

/** Fetch/XHR breadcrumbs keep a presigned S3 request's host and path, never its signature. */
export function stripPresignedBreadcrumbUrl(breadcrumb: Breadcrumb): Breadcrumb {
  const url = breadcrumb.data?.url;
  const isRequest = breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr';
  if (!isRequest || typeof url !== 'string' || !isPresignedUrl(url)) {
    return breadcrumb;
  }
  return { ...breadcrumb, data: { ...breadcrumb.data, url: stripPresignedQuery(url) } };
}
